/*
 * ScheduleEditorRenderer — Wochenprogramm week-grid editor.
 *
 * Grid: 7 columns (Mo/Di/Mi/Do/Fr/Sa/So) × N rows (24 hours × subrows per
 * `gridStepMinutes`). Cells are coloured by mode: comfort = accent,
 * reduced = muted warm, off = empty. Read-only mode paints cells grey with
 * cursor-default.
 *
 * Edit interactions (when !readOnly):
 *   - Drag on empty cells → new comfort block
 *   - Click existing block → cycle mode (comfort → reduced → off = delete)
 *   - Drag on cells that overlap an existing block → truncate + insert
 * On drop → serialise the internal grid model back to the wire-shape
 * `{ mon: [{start,end,mode}], tue: [...], ... }` and stream a write via
 * `source.write(programPoint, next)`. A pending marker shows until confirmed.
 *
 * JSON parse strategy: strict-with-fallback. `parseProgram()` accepts the
 * documented shape and returns `{ kind: 'unknown' }` on any structural
 * mismatch. The grid stays interactive (empty) and a "Programm-Format
 * unbekannt" chip is shown — never crash.
 *
 * Internal representation: blocks store minutes-since-midnight (0..1440) so
 * changing `gridStepMinutes` does not require re-quantising the model. Slot
 * ids are only used for pointer input translation.
 *
 * Optional Ferien-Overlay via `holidayPoint`: if any date range in the
 * holiday payload covers today, a small "Ferien aktiv" chip shows.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import type { ScheduleEditorWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import './ScheduleEditorRenderer.css';

interface Props {
  widget: ScheduleEditorWidget;
  source: IPointBindingSource;
}

type Mode = 'comfort' | 'reduced' | 'off';
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayKey = (typeof DAY_KEYS)[number];
const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Mo', tue: 'Di', wed: 'Mi', thu: 'Do', fri: 'Fr', sat: 'Sa', sun: 'So',
};

/** Block times are minutes-since-midnight (0..1440). Slot ids are only used
 *  for pointer maths — they translate to minutes on commit. */
interface Block { start: number; end: number; mode: Mode; }
type WeekProgram = Record<DayKey, Block[]>;

interface DragState {
  day: DayKey;
  startSlot: number;
  currentSlot: number;
  originBlockIdx?: number;
}

export function ScheduleEditorRenderer({ widget, source }: Props) {
  const stepMinutes = widget.gridStepMinutes && widget.gridStepMinutes > 0
    ? widget.gridStepMinutes
    : 15;
  const slotsPerHour = Math.max(1, Math.round(60 / stepMinutes));

  const programSig = useMemo(
    () => pointSignal(source, widget.programPoint),
    [source, widget.programPoint],
  );
  useEffect(() => programSig.release, [programSig]);

  const holidaySig = useMemo(
    () => (widget.holidayPoint ? pointSignal(source, widget.holidayPoint) : null),
    [source, widget.holidayPoint],
  );
  useEffect(() => holidaySig?.release ?? (() => {}), [holidaySig]);

  const raw = programSig.value.value;
  const parsed = useMemo(() => parseProgram(raw?.value), [raw]);

  // Local edit state: mirrors the parsed program while an edit is in flight.
  // Commit path drops `local` to null so the live signal wins after confirm.
  const [local, setLocal] = useState<WeekProgram | null>(null);
  const [pending, setPending] = useState<'sending' | 'rejected' | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const program = local ?? (parsed.kind === 'ok' ? parsed.program : emptyProgram());
  const holidayActive = useMemo(
    () => isHolidayActive(holidaySig?.value.value?.value),
    [holidaySig?.value.value],
  );

  const readOnly = !!widget.readOnly;

  function commit(next: WeekProgram) {
    setLocal(next);
    if (readOnly) return;
    setPending('sending');
    void writeProgram(source, widget.programPoint, next).then((state) => {
      if (state === 'rejected') {
        setPending('rejected');
        return;
      }
      // Confirmed: drop the local override so the live signal takes over
      // and clear the "sending" chip after a short tick.
      setLocal(null);
      window.setTimeout(() => setPending(null), 400);
    });
  }

  // ---- Drag lifecycle ---------------------------------------------------

  function onCellMouseDown(day: DayKey, slot: number, e: MouseEvent) {
    if (readOnly) return;
    e.preventDefault();
    const hit = blockAt(program[day], slot, slotsPerHour);
    setDrag({
      day,
      startSlot: slot,
      currentSlot: slot,
      originBlockIdx: hit ?? undefined,
    });
  }

  function onCellMouseEnter(day: DayKey, slot: number) {
    if (!drag || drag.day !== day) return;
    if (drag.currentSlot === slot) return;
    setDrag({ ...drag, currentSlot: slot });
  }

  useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      const isClick = drag.startSlot === drag.currentSlot;
      const next = cloneProgram(program);
      if (isClick && drag.originBlockIdx != null) {
        // Click on existing block → cycle: comfort → reduced → off (delete).
        const b = next[drag.day][drag.originBlockIdx];
        const nextMode: Mode | null =
          b.mode === 'comfort' ? 'reduced' :
            b.mode === 'reduced' ? 'off' : null;
        if (nextMode == null) next[drag.day].splice(drag.originBlockIdx, 1);
        else b.mode = nextMode;
      } else {
        const startS = Math.min(drag.startSlot, drag.currentSlot);
        const endS = Math.max(drag.startSlot, drag.currentSlot) + 1;
        const startMin = slotToMinutes(startS, slotsPerHour);
        const endMin = slotToMinutes(endS, slotsPerHour);
        next[drag.day] = mergeBlock(next[drag.day], {
          start: startMin,
          end: endMin,
          mode: 'comfort',
        });
      }
      commit(next);
      setDrag(null);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, program, slotsPerHour]);

  // ---- Render -----------------------------------------------------------

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div class={`hv-sched${readOnly ? ' hv-sched--readonly' : ''}`}>
      <header class="hv-sched__head">
        {widget.label && <div class="hv-sched__label">{widget.label}</div>}
        <div class="hv-sched__chips">
          {holidayActive && (
            <span class="hv-sched__chip hv-sched__chip--holiday" title="Ferienkalender aktiv">
              Ferien aktiv
            </span>
          )}
          {parsed.kind === 'unknown' && (
            <span class="hv-sched__chip hv-sched__chip--warn"
                  title="Der Regler liefert das Programm in einem unbekannten Format.">
              Programm-Format unbekannt
            </span>
          )}
          {pending === 'sending' && (
            <span class="hv-sched__chip hv-sched__chip--pending">Speichere …</span>
          )}
          {pending === 'rejected' && (
            <span class="hv-sched__chip hv-sched__chip--rejected">Abgelehnt</span>
          )}
        </div>
      </header>

      <div class="hv-sched__grid" role="grid" aria-label="Wochenprogramm">
        <div class="hv-sched__corner" />
        {DAY_KEYS.map((d) => (
          <div key={d} class="hv-sched__day-header" role="columnheader">{DAY_LABELS[d]}</div>
        ))}

        {hours.map((h) => (
          <ScheduleHourRow
            key={h}
            hour={h}
            program={program}
            slotsPerHour={slotsPerHour}
            drag={drag}
            readOnly={readOnly}
            onCellDown={onCellMouseDown}
            onCellEnter={onCellMouseEnter}
          />
        ))}
      </div>

      <footer class="hv-sched__legend">
        <span class="hv-sched__legend-item">
          <span class="hv-sched__swatch hv-sched__swatch--comfort" />
          Komfort
        </span>
        <span class="hv-sched__legend-item">
          <span class="hv-sched__swatch hv-sched__swatch--reduced" />
          Absenkung
        </span>
        <span class="hv-sched__legend-item">
          <span class="hv-sched__swatch hv-sched__swatch--off" />
          Aus
        </span>
      </footer>
    </div>
  );
}

// ---------- Row -----------------------------------------------------------

function ScheduleHourRow({
  hour, program, slotsPerHour, drag, readOnly, onCellDown, onCellEnter,
}: {
  hour: number;
  program: WeekProgram;
  slotsPerHour: number;
  drag: DragState | null;
  readOnly: boolean;
  onCellDown: (day: DayKey, slot: number, e: MouseEvent) => void;
  onCellEnter: (day: DayKey, slot: number) => void;
}) {
  const subs = Array.from({ length: slotsPerHour }, (_, i) => i);
  return (
    <>
      <div class="hv-sched__hour-header" role="rowheader">
        {String(hour).padStart(2, '0')}:00
      </div>
      {DAY_KEYS.map((day) => (
        <div key={day} class="hv-sched__day-col">
          {subs.map((sub) => {
            const slot = hour * slotsPerHour + sub;
            const mode = modeAt(program[day], slot, slotsPerHour);
            const previewing =
              !!drag && drag.day === day &&
              slot >= Math.min(drag.startSlot, drag.currentSlot) &&
              slot <= Math.max(drag.startSlot, drag.currentSlot);
            const cellMode = previewing ? 'comfort' : mode;
            return (
              <div
                key={slot}
                class={`hv-sched__cell hv-sched__cell--${cellMode ?? 'empty'}${previewing ? ' hv-sched__cell--preview' : ''}${readOnly ? ' hv-sched__cell--readonly' : ''}`}
                onMouseDown={(e) => onCellDown(day, slot, e as unknown as MouseEvent)}
                onMouseEnter={() => onCellEnter(day, slot)}
                role="gridcell"
                aria-label={`${DAY_LABELS[day]} ${slotToTime(slot, slotsPerHour)}`}
              />
            );
          })}
        </div>
      ))}
    </>
  );
}

// ---------- program parsing / serialisation ------------------------------

type ParseResult =
  | { kind: 'ok'; program: WeekProgram }
  | { kind: 'unknown' };

/** Strict-with-fallback parser. Accepts the documented wire shape
 *  `{ mon: [{start:"HH:mm", end:"HH:mm", mode:"comfort"|"reduced"|"off"}], ... }`.
 *  Anything not structurally matching returns `{ kind: 'unknown' }` so the
 *  renderer shows an inline warning instead of crashing. */
function parseProgram(raw: unknown): ParseResult {
  if (raw == null) return { kind: 'ok', program: emptyProgram() };

  // Some controllers ship the program as a JSON-encoded string.
  let obj: unknown = raw;
  if (typeof obj === 'string') {
    try { obj = JSON.parse(obj); }
    catch { return { kind: 'unknown' }; }
  }

  if (typeof obj !== 'object' || obj === null) return { kind: 'unknown' };
  const src = obj as Record<string, unknown>;

  const out = emptyProgram();
  let sawAnyDay = false;
  for (const day of DAY_KEYS) {
    const dayVal = src[day];
    if (dayVal === undefined) continue;
    if (!Array.isArray(dayVal)) return { kind: 'unknown' };
    sawAnyDay = true;
    for (const item of dayVal) {
      if (typeof item !== 'object' || item === null) return { kind: 'unknown' };
      const rec = item as Record<string, unknown>;
      const start = parseTime(rec.start);
      const end = parseTime(rec.end);
      const mode = parseMode(rec.mode);
      if (start == null || end == null || mode == null || end <= start) {
        return { kind: 'unknown' };
      }
      out[day].push({ start, end, mode });
    }
    out[day].sort((a, b) => a.start - b.start);
  }
  // If the object has neither day keys nor is empty, treat as unknown.
  if (!sawAnyDay && Object.keys(src).length > 0) return { kind: 'unknown' };
  return { kind: 'ok', program: out };
}

function serialiseProgram(p: WeekProgram): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const day of DAY_KEYS) {
    out[day] = p[day].map((b) => ({
      start: minutesToTime(b.start),
      end: minutesToTime(b.end),
      mode: b.mode,
    }));
  }
  return out;
}

function parseTime(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 24 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function parseMode(v: unknown): Mode | null {
  if (v === 'comfort' || v === 'reduced' || v === 'off') return v;
  return null;
}

function slotToTime(slot: number, slotsPerHour: number): string {
  return minutesToTime(slotToMinutes(slot, slotsPerHour));
}

function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function slotToMinutes(slot: number, slotsPerHour: number): number {
  return Math.round(slot * (60 / slotsPerHour));
}

function emptyProgram(): WeekProgram {
  return { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
}

function cloneProgram(p: WeekProgram): WeekProgram {
  const out = emptyProgram();
  for (const d of DAY_KEYS) out[d] = p[d].map((b) => ({ ...b }));
  return out;
}

function modeAt(blocks: Block[], slot: number, slotsPerHour: number): Mode | null {
  const minutes = slotToMinutes(slot, slotsPerHour);
  for (const b of blocks) {
    if (minutes >= b.start && minutes < b.end) return b.mode;
  }
  return null;
}

function blockAt(blocks: Block[], slot: number, slotsPerHour: number): number | null {
  const minutes = slotToMinutes(slot, slotsPerHour);
  for (let i = 0; i < blocks.length; i++) {
    if (minutes >= blocks[i].start && minutes < blocks[i].end) return i;
  }
  return null;
}

/** Merge a new block into a day, truncating any existing overlap instead of
 *  discarding it. Matches operator intent when a comfort band gets dragged
 *  over an existing reduced band — the reduced parts either side survive. */
function mergeBlock(blocks: Block[], next: Block): Block[] {
  const result: Block[] = [];
  for (const b of blocks) {
    if (b.end <= next.start || b.start >= next.end) {
      result.push(b);
      continue;
    }
    if (b.start < next.start) result.push({ ...b, end: next.start });
    if (b.end > next.end) result.push({ ...b, start: next.end });
  }
  result.push(next);
  result.sort((a, b) => a.start - b.start);
  return result;
}

async function writeProgram(
  source: IPointBindingSource,
  pointId: string,
  program: WeekProgram,
): Promise<'confirmed' | 'rejected'> {
  const wire = serialiseProgram(program);
  const ac = new AbortController();
  try {
    for await (const ack of source.write(pointId, wire, ac.signal)) {
      if (ack.state === 'confirmed') return 'confirmed';
      if (ack.state === 'rejected') return 'rejected';
    }
    return 'confirmed';
  } catch {
    return 'rejected';
  }
}

// ---------- holiday overlay ---------------------------------------------

function isHolidayActive(raw: unknown): boolean {
  // Accept the shapes seen across HVAC controllers:
  //   1. boolean → active today yes/no
  //   2. { active: bool }
  //   3. [{ from: "YYYY-MM-DD", to: "YYYY-MM-DD" }, ...]
  //   4. { ranges: [{ from, to }, ...] }
  if (raw == null) return false;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    try { return isHolidayActive(JSON.parse(raw)); } catch { return false; }
  }
  if (Array.isArray(raw)) return coversToday(raw);
  if (typeof raw === 'object') {
    const rec = raw as Record<string, unknown>;
    if (typeof rec.active === 'boolean') return rec.active;
    if (Array.isArray(rec.ranges)) return coversToday(rec.ranges);
  }
  return false;
}

function coversToday(ranges: unknown[]): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = today.getTime();
  for (const r of ranges) {
    if (typeof r !== 'object' || r === null) continue;
    const rec = r as Record<string, unknown>;
    const from = typeof rec.from === 'string' ? Date.parse(rec.from) : NaN;
    const to = typeof rec.to === 'string' ? Date.parse(rec.to) : NaN;
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    // to is inclusive-day → extend by 24h so "to = today" still counts.
    if (t >= from && t <= to + 24 * 60 * 60_000) return true;
  }
  return false;
}
